import { createSelector, createSlice } from "@reduxjs/toolkit";
import { sortDataBy } from "../../../SharedModule/utils/dataSort";
import { createChangeOrderReducer } from "../../../SharedModule/utils/changeOrderReducer";
import { ClientRelationshipReview } from "../../types/clients-module-types";
import { getAllCostCentersIds } from "../../utils/clientsUtils";
import { CostCenterDropdown } from "../../../UsersModule/types/users-module-types";
import { addElementToArray } from "../../../TimesModule/utils/timesUtils";

type ClientRelationshipReviewsListState = {
  reviewsList: ClientRelationshipReview[];
  orderedBy: {
    orderBy: string;
    criteria: "asc" | "desc";
    dataType?: "string";
  };
  isShowingActives: boolean;
  isShowingActivesInTab: boolean;
  isLoadingReviews: boolean;
  reviewsLoaded: boolean;
  applySupervisorCheck: boolean;
  costCenterOwnedIds: number[];
  isLoadingAllCostCenters: boolean;
  isLoadedAllCostCenters: boolean;
  allCostCenters: CostCenterDropdown[];
};

const initialState: ClientRelationshipReviewsListState = {
  reviewsList: [],
  orderedBy: {
    orderBy: "name",
    criteria: "asc",
    dataType: "string",
  },
  isShowingActives: true,
  isShowingActivesInTab: true,
  isLoadingReviews: true,
  reviewsLoaded: false,
  applySupervisorCheck: false,
  costCenterOwnedIds: [],
  isLoadingAllCostCenters: true,
  isLoadedAllCostCenters: false,
  allCostCenters: [],
};

// use this reducer for relationship reviews list page and tab page
export const clientRelationshipReviewsListSlice = createSlice({
  name: "clientRelationshipReviewList",
  initialState,
  reducers: {
    loadReviewsInfo: (state, action) => {
      state.reviewsList = action.payload.data;
      state.reviewsList = state.reviewsList.map((rr) => {
        let clientFound = action.payload.clients.find(
          (elem) => elem.id === rr.clientId
        );
        return {
          ...rr,
          clientActive: clientFound ? clientFound.isActive : false,
        };
      });
      state.isLoadingReviews = false;
      state.reviewsLoaded = true;
    },
    loadCostCentersOwned: (state, action) => {
      state.applySupervisorCheck = action.payload.applySupervisorCheck;
      state.costCenterOwnedIds = getAllCostCentersIds(
        action.payload.costCentersOwned
      );
    },
    loadAllCostCenters: (state, action) => {
      state.allCostCenters = [];
      addElementToArray(state.allCostCenters, action.payload);
      state.isLoadingAllCostCenters = false;
      state.isLoadedAllCostCenters = true;
    },
    setIsLoadingAllCostCenters: (state, action) => {
      state.isLoadingAllCostCenters = action.payload;
    },
    setIsLoadingReviews: (state, action) => {
      state.isLoadingReviews = action.payload;
    },
    setShowActives: (state, action) => {
      state.isShowingActives = action.payload;
    },
    setShowActivesInTab: (state, action) => {
      state.isShowingActivesInTab = action.payload;
    },
    changeOrder: createChangeOrderReducer("orderedBy"),
  },
});

// Action creators are generated for each case reducer function
export const {
  loadReviewsInfo,
  loadCostCentersOwned,
  loadAllCostCenters,
  setIsLoadingAllCostCenters,
  setIsLoadingReviews,
  setShowActives,
  setShowActivesInTab,
  changeOrder,
} = clientRelationshipReviewsListSlice.actions;

export const IsLoadingReviews = (state: any) => {
  return state.clientRelationshipReviewList.isLoadingReviews;
};

export const IsReviewsLoaded = (state: any) => {
  return state.clientRelationshipReviewList.reviewsLoaded;
};

export const IsLoadingAllCostCenters = (state: any) => {
  return state.clientRelationshipReviewList.isLoadingAllCostCenters;
};

export const IsLoadedAllCostCenters = (state: any) => {
  return state.clientRelationshipReviewList.isLoadedAllCostCenters;
};

export const SelectAllCostCenter = (state: any) => {
  return state.clientRelationshipReviewList.allCostCenters;
};

export const IsApplySupervisorCheck = (state: any) => {
  return state.clientRelationshipReviewList.applySupervisorCheck;
};

export const SelectCostCenterOwnedIds = (state: any) => {
  return state.clientRelationshipReviewList.costCenterOwnedIds;
};

export const SelectRelationshipReviews = ({
  clientRelationshipReviewList,
}: {
  clientRelationshipReviewList: ClientRelationshipReviewsListState;
}) => {
  return { reviewsList: clientRelationshipReviewList.reviewsList };
};

export const IsShowingActives = (state: any) => {
  return state.clientRelationshipReviewList.isShowingActives;
};

export const IsShowingActivesInTab = (state: any) => {
  return state.clientRelationshipReviewList.isShowingActivesInTab;
};

export const SelectOrderCriteria = ({
  clientRelationshipReviewList,
}: {
  clientRelationshipReviewList: ClientRelationshipReviewsListState;
}) => clientRelationshipReviewList.orderedBy;

export const SelectOrderedByReviewsList = createSelector(
  [SelectRelationshipReviews, SelectOrderCriteria],
  (review, orderCriteria) => {
    return {
      reviewsList:
        (review.reviewsList &&
          sortDataBy(
            review.reviewsList,
            orderCriteria.orderBy,
            orderCriteria.criteria,
            orderCriteria.dataType
          )) ||
        [],
    };
  }
);

export default clientRelationshipReviewsListSlice.reducer;
